/**
 * Aba "Radar de Estilo Raiz" da ficha: a pontuação de cada estilo, em tabela e em radar, calculada
 * no navegador a partir do Questionário Geral e da Personalidade.
 *
 * Porte de `portal/admin.html:2617-2619` (a aba), `4922-5018` (cálculo e desenho) e `5020-5046`
 * (a leitura dos dois questionários). Linha do inventário: 194.
 *
 * Território da frente A2. A moldura (`../index.tsx`) importa esta aba pelo nome `AbaRadar`.
 */

// Estado e retorno estável do React.
import { useCallback, useState } from 'react';
// A tabela do Chassi.
import { Tabela } from '@complexity/ui';
// As duas leituras que o radar cruza.
import { lerPersonalidade, lerQuestionarioGeral } from '@studiolu/dados/areas/ficha';
// A fonte de dados.
import { usePainel } from '../../../contexto.js';
// O cálculo e a geometria do legado.
import { calcularPontosRaiz, geometriaDoRadar, type PontoRaiz } from '../comum-ficha/regras.js';
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
// O contrato de carga: a moldura lê esta aba antes de mostrá-la e relê a cada toque no botão dela.
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// Os apoios comuns das abas (A3).
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';

export function AbaRadar({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada.
  if (!ficha) return <AbaSemFicha aba="Radar de Estilo Raiz" />;
  return <RadarDaCliente ficha={ficha} />;
}

function RadarDaCliente({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const escopo = useEscopoEstavel(ficha);
  // Os pontos calculados; `null` até as duas leituras voltarem.
  const [pontos, setPontos] = useState<readonly PontoRaiz[] | null>(null);

  // Lê os dois questionários e calcula, como `carregarQuestionarios` (`admin.html:5023-5046`).
  const carregar = useCallback(async () => {
    const geral = await lerQuestionarioGeral(dados, escopo);
    if (!geral.ok) console.error('Erro ao carregar questionário geral:', geral.erro);
    const personalidade = await lerPersonalidade(dados, escopo);
    if (!personalidade.ok) console.error('Erro ao carregar questionário de personalidade:', personalidade.erro);
    // Leitura que falhou conta como questionário não respondido, como o `data` nulo do legado.
    setPontos(
      calcularPontosRaiz(
        geral.ok ? (geral.dados as Readonly<Record<string, unknown>> | null) : null,
        personalidade.ok ? (personalidade.dados as Readonly<Record<string, unknown>> | null) : null,
      ),
    );
  }, [dados, escopo]);

  /*
   * A leitura pelo contrato da moldura (`comum-ficha/carregador.ts`): antes de a aba aparecer e a
   * cada toque no botão dela. É o que faz o radar aparecer já com a conta da vez: no legado, o
   * mesmo `carregarQuestionarios` do Questionário e da Personalidade redesenha o radar a cada
   * clique numa das quatro abas (`5044`), então voltar ao radar depois de marcar adjetivos mostra a
   * pontuação nova, e nunca a anterior.
   */
  useCarregadorDaAba(carregar);

  if (!pontos) return null;

  // Tudo zero: a frase do legado, e nem tabela nem gráfico (`admin.html:5006-5009`).
  if (pontos.every((p) => p.pontuacao === 0)) {
    return (
      <p className="text-cx-sm text-texto-suave">
        Sem dados suficientes pra calcular ainda. Falta responder o questionário geral e/ou o de personalidade.
      </p>
    );
  }

  const g = geometriaDoRadar(pontos);
  return (
    <div className="flex flex-col gap-cx-4" data-testid="corpo-radar-estilo-raiz">
      <Tabela
        rotulo="Radar de Estilo Raiz"
        colunas={[
          { chave: 'estilo', titulo: 'Estilo Raiz', celula: (p: PontoRaiz) => p.estilo },
          { chave: 'pontuacao', titulo: 'Pontuação Final', celula: (p: PontoRaiz) => p.pontuacao },
        ]}
        linhas={pontos}
        chaveDaLinha={(p) => p.estilo}
        vazio=""
      />
      {/* O radar, com as contas do legado no `viewBox` 0 0 300 300. As cores saem dos tokens: a
          grade e os eixos na cor de borda, o polígono e os rótulos na cor do texto, como
          `var(--border)` e `var(--dark)` do legado. */}
      <svg
        viewBox="0 0 300 300"
        role="img"
        aria-label="Radar de Estilo Raiz"
        className="mx-auto w-full max-w-xs"
        data-testid="radar-svg"
      >
        {g.grades.map((pontosDaGrade, i) => (
          <polygon
            key={i}
            points={pontosDaGrade}
            fill="none"
            className="stroke-borda"
            strokeWidth={1}
            // Só a grade de fora é contínua; as de dentro são tracejadas (`admin.html:4957`).
            strokeDasharray={i === g.grades.length - 1 ? undefined : '3,3'}
          />
        ))}
        {g.eixos.map((e, i) => (
          <line key={i} x1={150} y1={150} x2={e.x} y2={e.y} className="stroke-borda" strokeWidth={1} />
        ))}
        <polygon points={g.dados} className="fill-texto stroke-texto" fillOpacity={0.16} strokeWidth={2} />
        {g.rotulos.map((r) => (
          <text key={r.texto} x={r.x} y={r.y} fontSize={10} textAnchor="middle" className="fill-texto font-sans">
            {r.texto}
          </text>
        ))}
      </svg>
    </div>
  );
}
